import { useEffect, useRef } from "react";

interface TelemetryOptions {
  pageId: string;
}

export function useLandingTelemetry({ pageId }: TelemetryOptions) {
  const startTime = useRef(Date.now());
  const hasScrolled = useRef(false);
  const sessionId = useRef(
    crypto.randomUUID ? crypto.randomUUID() : "sess-" + Date.now()
  );

  useEffect(() => {
    const handleScroll = () => {
      hasScrolled.current = true;
      window.removeEventListener("scroll", handleScroll);
    };
    window.addEventListener("scroll", handleScroll);

    const handleUnload = (event: Event) => {
      // If visibilitychange fired but we aren't hidden, don't trigger unload
      if (event.type === 'visibilitychange' && document.visibilityState !== 'hidden') {
        return;
      }
      
      const timeOnPageMs = Date.now() - startTime.current;
      
      // If they were on the page for less than 5 seconds and didn't scroll, it's a bounce
      const bounced = timeOnPageMs < 5000 && !hasScrolled.current;

      const telemetryData = {
        session_id: sessionId.current,
        page_id: pageId,
        time_on_page_ms: timeOnPageMs,
        bounced,
        cta_clicked: false // If we navigate away via window close/refresh, it wasn't a CTA click
      };

      // Use sendBeacon so the request goes out even as the tab is closing
      const blob = new Blob([JSON.stringify(telemetryData)], {
        type: 'application/json'
      });
      navigator.sendBeacon("/api/landing/telemetry", blob);
    };

    window.addEventListener("pagehide", handleUnload);
    document.addEventListener("visibilitychange", handleUnload);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("pagehide", handleUnload);
      document.removeEventListener("visibilitychange", handleUnload);
      handleUnload(new Event('unmount')); // Call on component unmount (React Router navigation)
    };
  }, [pageId]);

  const recordCtaClick = () => {
    const timeOnPageMs = Date.now() - startTime.current;
    const telemetryData = {
      session_id: sessionId.current,
      page_id: pageId,
      time_on_page_ms: timeOnPageMs,
      bounced: false, // By definition not a bounce if they clicked CTA
      cta_clicked: true
    };
    
    fetch("/api/landing/telemetry", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(telemetryData),
      keepalive: true
    }).catch(e => console.error("Failed to send CTA telemetry", e));
  };

  return { recordCtaClick };
}
